/**
 * Reportes → Comprobantes por serie, producto y fecha (`/reportes/serie-fecha-producto`).
 *
 * Puerto de `Reportes::reporte_por_serie_fecha_producto` +
 * `Reportes_model::get_comprobante_by_serie_producto_fecha` +
 * `views/menu/reportes/reporte_serie_producto_fecha.php`.
 *
 * La pantalla del panel anterior pedía **fecha de inicio, fecha de fin, serie y
 * producto** (los dos últimos con TODOS) y pintaba un listado por línea: ID de la
 * venta, serie, fecha, cliente, producto, cantidad, precio, total, grifero y estado,
 * con la suma de cantidad y total en el pie y un export a Excel del navegador.
 *
 * Reglas conservadas:
 * - El listado une los **cuatro bloques** del original: facturas y boletas
 *   (`venta_producto` + `users` del turno), las ventas **administradas**
 *   (`admin_venta_detalle` + `admin_comprobante`, donde vive la serie) y las **notas
 *   de crédito** (`nota_credito_detalle`).
 * - Por defecto sólo salen los comprobantes **aceptados** (`estado_api = 'Aceptado'`
 *   y, en la venta administrada, `estado_sunat = 'Aceptado'`), como en el original;
 *   la casilla *incluir no aceptados* los abre.
 * - La fila **anulada** viaja con cantidad y total en cero (era el apaño del
 *   navegador) y el pie suma cantidad y total.
 *
 * Diferencias anotadas:
 * - Se añaden los totales **por producto** y **por serie** (el original sólo tenía el
 *   pie de la tabla), y el CSV y el PDF los sirve la API.
 * - En el bloque de notas de crédito el cliente ya no se fuerza al identificador 6
 *   (el apaño del modelo anterior): la nota no guarda cliente, así que la columna
 *   sale vacía.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosSerieFechaProducto,
  useSerieFechaProducto,
  type FiltrosSerieFechaProducto,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Los colores del origen del comprobante (factura, boleta, administrada o nota). */
const ORIGENES: Record<string, 'neutral' | 'brand' | 'success' | 'warning'> = {
  FACTURA: 'brand',
  BOLETA: 'success',
  ADMINISTRADO: 'warning',
  'NOTA DE CREDITO': 'neutral',
};

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto del reporte: el mes en curso (el original abría con el día 1). */
function rangoPorDefecto(): FiltrosSerieFechaProducto {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
    serie: '',
    producto: '',
    todos: false,
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

export function ReporteSerieFechaProductoPage() {
  useDocumentTitle('Reportes · Comprobantes por serie, producto y fecha');

  const [filtros, setFiltros] = useState<FiltrosSerieFechaProducto>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosSerieFechaProducto();
  const consulta = useSerieFechaProducto(filtros);
  const datos = consulta.data;

  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  // El listado puede traer miles de líneas (todo el mes por producto): se pagina en el
  // navegador y los totales del pie siguen saliendo del listado completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porProducto = datos?.por_producto ?? [];
  const porSerie = datos?.por_serie ?? [];
  const resumen = datos?.resumen;

  const series = catalogos.data?.series ?? [];
  const productos = catalogos.data?.productos ?? [];

  const cambiar = <C extends keyof FiltrosSerieFechaProducto>(
    campo: C,
    valor: FiltrosSerieFechaProducto[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarSerieFechaProducto(filtros), {
        nombre: `serie-fecha-producto-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfSerieFechaProducto(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const productosDeLaSerie = useMemo(() => {
    if (productos.length === 0) return [];

    const deLaSerie = porProducto.map((fila) => fila.producto_id);
    const conLineas = productos.filter((producto) => deLaSerie.includes(producto.id));

    return conLineas.length > 0 ? conLineas : productos;
  }, [porProducto, productos]);

  const meses = catalogos.data?.meses ?? [];

  const rangoDe = (mes: string) => {
    const anio = Number(mes.slice(0, 4));
    const numero = Number(mes.slice(5, 7));

    return { desde: `${mes}-01`, hasta: fechaLocal(new Date(anio, numero, 0)) };
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">
            Comprobantes por serie, producto y fecha
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Las líneas vendidas de una serie y un producto, con su comprobante y su grifero.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Serie</span>
              <Select
                value={filtros.serie}
                onChange={({ target }) => cambiar('serie', target.value)}
                className={CONTROL}
                aria-label="Serie"
              >
                <option value="">TODAS</option>
                {series.map((serie) => (
                  <option key={`${serie.serie}-${serie.origen}`} value={serie.serie ?? ''}>
                    {serie.serie} · {serie.origen} ({formatNumber(serie.comprobantes)})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.producto}
                onChange={({ target }) => cambiar('producto', target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                <option value="">TODOS</option>
                {productosDeLaSerie.map((producto) => (
                  <option key={producto.id} value={String(producto.id)}>
                    {producto.nombre} {producto.tipo ? `· ${producto.tipo}` : ''}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4 border-t border-ink-100 pt-3">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
              <input
                type="checkbox"
                className="size-4 accent-brand-600"
                checked={filtros.todos}
                onChange={(evento) => cambiar('todos', evento.target.checked)}
              />
              Incluir comprobantes no aceptados
            </label>

            {meses.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                  <RefreshCw className="size-3.5" aria-hidden="true" />
                  Meses con líneas
                </span>
                {meses.slice(0, 12).map((mes) => {
                  const rango = rangoDe(mes.periodo);
                  const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                  return (
                    <button
                      key={mes.periodo}
                      type="button"
                      aria-pressed={activo}
                      onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        activo
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                      title={`${formatNumber(mes.lineas)} línea(s)`}
                    >
                      {etiquetaMes(mes.periodo)}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Líneas',
            valor: formatNumber(resumen?.lineas ?? 0),
            detalle: `${formatNumber(resumen?.anuladas ?? 0)} anulada(s)`,
          },
          {
            etiqueta: 'Comprobantes',
            valor: formatNumber(resumen?.comprobantes ?? 0),
            detalle: `${formatNumber(porSerie.length)} serie(s)`,
          },
          {
            etiqueta: 'Cantidad',
            valor: formatNumber(resumen?.cantidad ?? 0),
            detalle: 'galones y unidades',
          },
          {
            etiqueta: 'Base',
            valor: formatCurrency(resumen?.subtotal ?? 0),
            detalle: 'sin IGV',
          },
          {
            etiqueta: 'Total',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: 'de las líneas',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por producto</h2>

            {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

            {!consulta.isLoading ? (
              <div className="max-h-[40vh] overflow-y-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Producto</th>
                      <th className="w-[4.5rem] px-2 py-2 text-right">Líneas</th>
                      <th className="w-[5.5rem] px-2 py-2 text-right">Cantidad</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porProducto.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={4} className="px-2 py-8 text-center text-sm text-ink-500">
                          No hay líneas que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {porProducto.map((fila) => (
                      <tr
                        key={fila.producto_id}
                        className="border-t border-ink-100 hover:bg-brand-50/30"
                      >
                        <td className="px-2 py-2">
                          <p className="truncate font-semibold text-ink-900" title={fila.producto ?? ''}>
                            {fila.producto ?? '—'}
                          </p>
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.lineas)}
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.cantidad)}
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-ink-900">
                          {formatCurrency(fila.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por serie</h2>

            {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

            {!consulta.isLoading ? (
              <div className="max-h-[40vh] overflow-y-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Serie</th>
                      <th className="w-[5.5rem] px-2 py-2 text-right">Comprob.</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porSerie.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={3} className="px-2 py-8 text-center text-sm text-ink-500">
                          No hay comprobantes que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {porSerie.map((fila) => (
                      <tr key={fila.serie} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td className="px-2 py-2">
                          <p className="font-semibold text-ink-900">{fila.serie ?? '—'}</p>
                          <Badge
                            variant={ORIGENES[fila.origen] ?? 'neutral'}
                            className="mt-0.5 px-1.5 py-0 text-[10px]"
                          >
                            {fila.origen}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.comprobantes)}
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-ink-900">
                          {formatCurrency(fila.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Líneas del periodo ({formatNumber(resumen?.lineas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)} ·{' '}
              {filtros.todos ? 'todos los comprobantes' : 'sólo comprobantes aceptados'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
              <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[3.5rem] px-1.5 py-2">ID</th>
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Comprobante</th>
                    <th className="px-1.5 py-2">Cliente</th>
                    <th className="px-1.5 py-2">Producto</th>
                    <th className="w-[4.5rem] px-1.5 py-2 text-right">Cant.</th>
                    <th className="w-[5.25rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[7rem] px-1.5 py-2">Grifero</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay líneas que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr
                      key={`${fila.origen}-${fila.comprobante}-${fila.producto_id}-${fila.venta_id}`}
                      className={cn(
                        'border-t border-ink-100 hover:bg-brand-50/30',
                        fila.anulado && 'text-ink-400',
                      )}
                    >
                      <td className="px-1.5 py-2 text-ink-600">{fila.venta_id}</td>
                      <td className="px-1.5 py-2 text-ink-700">{formatDateValue(fila.fecha)}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">{fila.comprobante}</p>
                        <Badge
                          variant={ORIGENES[fila.origen] ?? 'neutral'}
                          className="mt-0.5 px-1.5 py-0 text-[10px]"
                        >
                          {fila.origen}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        {fila.estado && !fila.anulado ? (
                          <p className="truncate text-[11px] text-ink-500">{fila.estado}</p>
                        ) : null}
                        {fila.anulado ? (
                          <p className="text-[11px] font-semibold text-rose-700">ANULADO</p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.producto ?? ''}>
                          {fila.producto ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {formatCurrency(fila.precio_unidad)} / u.
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatNumber(fila.cantidad)}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-700" title={fila.grifero ?? ''}>
                          {fila.grifero ?? '—'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={5}>
                        SUMA · {formatNumber(resumen?.lineas ?? 0)} línea(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.cantidad ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="líneas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          El listado une las <strong>facturas</strong>, las <strong>boletas</strong>, las ventas{' '}
          <strong>administradas</strong> (las de NUBE, donde la serie vive en el comprobante
          administrado) y las <strong>notas de crédito</strong>; por defecto sólo salen los
          comprobantes aceptados, como en el panel anterior. La fila anulada se ve con cantidad y
          total en cero y no suma al pie.
        </span>
      </p>
    </div>
  );
}
